import { Directive, ElementRef, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, Renderer2 } from '@angular/core';

@Directive({
  selector: '[appScrollSpy]'
})
export class ScrollSpyDirective implements OnInit, OnDestroy {
  /** Selector for sections inside the scroll container (must carry data-section-id). */
  @Input() spySelector: string = '[data-section-id]';

  /** Sticky header element (used to compute offset and set scrollPaddingTop). */
  @Input() spyHeader?: HTMLElement | ElementRef<HTMLElement>;

  /** Fallback fixed header offset (px) if no spyHeader is provided. */
  @Input() spyHeaderOffset = 0;

  @Input() enabled = true;

  /** Emits whenever the active section id changes. */
  @Output() activeChange = new EventEmitter<string | null>();

  /** Emits a lightweight tick while the user is scrolling (useful to drive header nudging). */
  @Output() userScrollTick = new EventEmitter<void>();

  private host!: HTMLElement;
  private headerEl?: HTMLElement;
  private headerH = 0;
  private rafPending = false;
  private ro?: ResizeObserver;
  private wheelListener?: () => void;
  private touchListener?: () => void;
  private keyListener?: () => void;
  private userScrollTimer: any;

  constructor(private el: ElementRef<HTMLElement>, private zone: NgZone, private renderer: Renderer2) {}

  ngOnInit(): void {
    this.host = this.el.nativeElement;
    if (!this.enabled) return;

    // Resolve header element if provided
    if (this.spyHeader instanceof ElementRef) {
      this.headerEl = this.spyHeader.nativeElement;
    } else if (this.spyHeader instanceof HTMLElement) {
      this.headerEl = this.spyHeader;
    }

    this.updateHeaderHeight();
    // Reserve space so scroll-to-section doesn't hide under sticky header.
    this.renderer.setStyle(this.host, 'scrollPaddingTop', `${this.headerH}px`);

    // Observe header size changes (responsive)
    if (this.headerEl && 'ResizeObserver' in window) {
      this.ro = new ResizeObserver(() => {
        this.updateHeaderHeight();
        this.renderer.setStyle(this.host, 'scrollPaddingTop', `${this.headerH}px`);
        this.updateActiveFromRects(); // keep highlight accurate
      });
      this.ro.observe(this.headerEl);
    }

    // Listen to scroll (container is the host)
    this.zone.runOutsideAngular(() => {
      this.host.addEventListener('scroll', this.onScroll, { passive: true });

      // Mark “user scroll” window for smoother UX
      const markUser = () => {
        clearTimeout(this.userScrollTimer);
        this.userScrollTick.emit(); // emit immediately
        this.userScrollTimer = setTimeout(() => {}, 120); // short-lived window
      };
      this.wheelListener = this.renderer.listen(this.host, 'wheel', markUser);
      this.touchListener = this.renderer.listen(this.host, 'touchstart', markUser);
      this.keyListener = this.renderer.listen(this.host, 'keydown', markUser);
    });

    // Seed once after initial layout
    requestAnimationFrame(() => this.updateActiveFromRects());
  }

  ngOnDestroy(): void {
    this.host.removeEventListener('scroll', this.onScroll as any);
    this.wheelListener?.();
    this.touchListener?.();
    this.keyListener?.();
    this.ro?.disconnect();
    clearTimeout(this.userScrollTimer);
  }

  /** rAF-throttled scroll handler (kept outside Angular). */
  private onScroll = () => {
    if (!this.enabled) return;
    if (this.rafPending) return;
    this.rafPending = true;
    requestAnimationFrame(() => {
      this.rafPending = false;
      this.updateActiveFromRects();
      // Let listeners (e.g., header) react while user scrolls
      this.zone.run(() => this.userScrollTick.emit());
    });
  };

  private updateHeaderHeight(): void {
    this.headerH = this.headerEl ? this.headerEl.getBoundingClientRect().height : this.spyHeaderOffset;
  }

  private updateActiveFromRects(): void {
    const sections = Array.from(this.host.querySelectorAll<HTMLElement>(this.spySelector));
    if (sections.length === 0) {
      this.activeChange.emit(null);
      return;
    }

    const anchorY = this.host.getBoundingClientRect().top + this.headerH + 1;

    // Pick the last section whose top is above anchor line
    let active: string | null = sections[0].dataset['sectionId'] ?? null;
    for (const el of sections) {
      const top = el.getBoundingClientRect().top;
      if (top <= anchorY) {
        active = el.dataset['sectionId'] ?? active;
      } else {
        break;
      }
    }

    this.zone.run(() => this.activeChange.emit(active));
  }
}
